import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cmsAPI, getImageUrl } from '../utils/api';
import '../styles/pages/Collaborations.scss';

// --- Default Fallbacks ---
const defaultCompanyPartners = [
  {
    id: 1,
    name: 'Innovations Hub',
    logo: '/images/logo-innovationshub.png',
    description: 'Partnership on digital solution strategy, implementation support, and business process modernization initiatives.',
    website: 'https://innovationshub.in/'
  },
  {
    id: 2,
    name: 'Aimers Infotech',
    logo: '/images/logo-aimersinfotech.png',
    description: 'Partnership for web development delivery, academic technology support, and digital outreach programs.',
    website: 'https://aimersinfotech.in/Frontend/index'
  },
  {
    id: 3,
    name: 'Rydes',
    logo: '/images/logo-rydes.png',
    description: 'Partnership on mobility-focused digital products, platform optimization, and customer experience enhancements.',
    website: 'https://rydes.in/'
  },
  {
    id: 4,
    name: 'SPCL Infotech Services Pvt. Ltd. Pune',
    logo: '/images/logo-spcl.png',
    description: 'Partnership for software development, IT services, and corporate technology training solutions.',
    website: ''
  }
];

const defaultCsrPartners = [
  { id: 1, name: 'Medhavi Foundation', logo: '/images/logo-medhavi.png' },
  { id: 2, name: 'Amrutvahini College', logo: '/images/logo-amrutvahini.png' },
  { id: 3, name: 'Savitribai Phule Pune University', logo: '/images/logo-sppu.png' },
  { id: 4, name: 'District Administration', logo: '/images/logo-maharashtra.png' },
  { id: 5, name: 'Zilla Parishad Ahmednagar', logo: '/images/logo-zillaparishad.png' },
  { id: 6, name: 'MAHADCOM Government', logo: '/images/logo-mahadcom.png' },
  { id: 7, name: 'NSDC India', logo: '/images/logo-nsdc.png' }
];

const defaultWhyPartnerFeatures = [
  {
    icon: '🚀',
    title: 'End-to-End Execution',
    description: 'From project conceptualization and planning to on-ground implementation and impact measurement, we handle everything.'
  },
  {
    icon: '📊',
    title: 'Measurable Impact',
    description: 'Every project includes defined KPIs, regular progress tracking, and comprehensive impact assessment reports for stakeholders.'
  },
  {
    icon: '🏆',
    title: 'Technology-Driven',
    description: 'We leverage modern technology solutions — mobile apps, web platforms, data analytics — to maximize project reach and effectiveness.'
  },
  {
    icon: '🤝',
    title: 'Local Expertise',
    description: 'Deep understanding of rural communities in Maharashtra, strong local networks, and culturally sensitive approach to project delivery.'
  }
];

const defaultProjects = [
  {
    title: 'Medhavi Foundation — Digital Literacy & Skill Development Program',
    image: '/images/collab-medhavi.jpg',
    description: 'In collaboration with Medhavi Foundation and funded through CSR initiatives, we developed and implemented a comprehensive digital literacy and skill development program for rural youth in Ahmednagar district. The program equipped young individuals with essential computer skills, IT fundamentals, and vocational training to enhance their employability.',
    details: 'The project included setting up digital learning centres in rural areas, conducting hands-on training workshops, and providing certification upon completion. Over 500+ students benefited from this initiative, with many securing employment opportunities in the IT and services sector.',
    highlights: [
      { number: '500+', label: 'Students Trained' },
      { number: '12', label: 'Rural Centres' },
      { number: '85%', label: 'Placement Rate' }
    ],
    reverse: false
  }
];

function Collaborations() {
  const navigate = useNavigate();

  // State elements loading from API
  const [companyPartners, setCompanyPartners] = useState([]);
  const [csrPartners, setCsrPartners] = useState([]);
  const [whyPartnerFeatures, setWhyPartnerFeatures] = useState([]);
  const [projects, setProjects] = useState([]);

  // Sections copywriting config
  const [sections, setSections] = useState({
    collaborations_hero: {
      title: 'Our Collaborations',
      subtitle: 'Partnering with leading organisations on impactful CSR-funded projects for rural development, education, and digital empowerment.',
      image: '/images/collab-hero.jpg'
    },
    collaborations_csr_intro: {
      title: 'Driving Impact Through Corporate Social Responsibility',
      subtitle: 'CSR Partnerships',
      description: 'At Pravara Infotech, we believe technology can create meaningful change at the grassroots level. Through strategic collaborations with leading organisations and CSR fund allocation, we have successfully executed projects focused on rural development, digital literacy, skill development, and education in underserved communities across Maharashtra.\n\nOur CSR projects bridge the gap between corporate resources and community needs, ensuring that every rupee allocated creates measurable, lasting impact. We work closely with our partners from project planning to execution and impact assessment.',
      image: '/images/collab-csr.jpg'
    },
    collaborations_why_partner_intro: {
      title: 'Why Partner With Pravara Infotech?',
      subtitle: 'Why Collaborate',
      description: 'We bring technical expertise, on-ground execution capability, and a proven track record of delivering impactful CSR projects that create measurable change.'
    },
    collaborations_cta: {
      title: "Let's Build Impact Together",
      description: "Interested in partnering with us for CSR projects? We'd love to discuss how we can create meaningful change together through technology and collaboration.",
      image: '/images/cta-bg.jpg'
    }
  });

  useEffect(() => {
    loadCollaborationsData();
  }, []);

  const loadCollaborationsData = async () => {
    try {
      // 1. Fetch HomeSections for titles & banners
      const sectionsRes = await cmsAPI.getHome();
      if (sectionsRes.data.success) {
        const sectionsData = sectionsRes.data.data;
        setSections(prev => {
          const updated = { ...prev };
          sectionsData.forEach(item => {
            if (item.section_key.startsWith('collaborations_')) {
              updated[item.section_key] = item;
            }
          });
          return updated;
        });
      }

      // 2. Fetch lists
      const companyRes = await cmsAPI.getCompanyPartners();
      if (companyRes.data.success && companyRes.data.data.length > 0) {
        setCompanyPartners(companyRes.data.data);
      } else {
        setCompanyPartners(defaultCompanyPartners);
      }

      const csrRes = await cmsAPI.getCsrPartners();
      if (csrRes.data.success && csrRes.data.data.length > 0) {
        setCsrPartners(csrRes.data.data);
      } else {
        setCsrPartners(defaultCsrPartners);
      }

      const featuresRes = await cmsAPI.getCollaborationFeatures();
      if (featuresRes.data.success && featuresRes.data.data.length > 0) {
        setWhyPartnerFeatures(featuresRes.data.data);
      } else {
        setWhyPartnerFeatures(defaultWhyPartnerFeatures);
      }

      const projectsRes = await cmsAPI.getCollaborationProjects();
      if (projectsRes.data.success && projectsRes.data.data.length > 0) {
        // Map backend projects, parsing highlights JSON
        const mappedProjects = projectsRes.data.data.map(proj => {
          let parsedHighlights = [];
          if (proj.highlights) {
            try {
              parsedHighlights = typeof proj.highlights === 'string' ? JSON.parse(proj.highlights) : proj.highlights;
            } catch (e) {
              console.error('Error parsing highlights:', e);
            }
          }
          return {
            ...proj,
            highlights: parsedHighlights
          };
        });
        setProjects(mappedProjects);
      } else {
        setProjects(defaultProjects);
      }

    } catch (err) {
      console.error('Failed to load collaborations dynamic content, fallback to static defaults:', err);
      setCompanyPartners(defaultCompanyPartners);
      setCsrPartners(defaultCsrPartners);
      setWhyPartnerFeatures(defaultWhyPartnerFeatures);
      setProjects(defaultProjects);
    }
  };

  const getBackgroundBanner = (secKey, defaultBanner) => {
    const sec = sections[secKey];
    if (sec && sec.image) {
      return getImageUrl(sec.image);
    }
    return defaultBanner;
  };

  return (
    <div className="collaborations">
      {/* Hero Section */}
      <section 
        className="collaborations-hero" 
        style={{ 
          backgroundImage: `linear-gradient(135deg, rgba(10, 37, 64, 0.85), rgba(0, 82, 106, 0.85)), url('${getBackgroundBanner('collaborations_hero', '/images/collab-hero.jpg')}')` 
        }}
      >
        <h1>{sections.collaborations_hero.title}</h1>
        <p>{sections.collaborations_hero.subtitle}</p>
      </section>

      {/* Trusted By Section */}
      <section className="trusted-by-section">
        <div className="trusted-container">
          <div className="text-center">
            <span className="section-tag">Our Partners</span>
            <h2 className="section-title">Trusted By</h2>
            <p className="section-desc">We are proud to collaborate with reputed organisations and government bodies to deliver impactful projects across Maharashtra.</p>
          </div>
          <p className="trusted-label">Organisations we have worked with</p>
          <div className="trusted-ticker-wrapper">
            <div className="trusted-ticker">
              {csrPartners.concat(csrPartners).map((logo, index) => (
                <div key={index} className="trusted-logo-item">
                  <img src={getImageUrl(logo.logo)} alt={logo.name} title={logo.name} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Partners Section */}
      <section className="company-partners-section">
        <div className="partners-container">
          <div className="text-center">
            <span className="section-tag">Our Partners</span>
            <h2 className="section-title">Our Company Partners</h2>
            <p className="section-desc">We partner with private organizations for technology, product, and growth initiatives.</p>
          </div>
          <div className="partners-grid">
            {companyPartners.map((partner) => (
              <div key={partner.id} className="partner-card">
                <div className="partner-logo">
                  {partner.logo && partner.logo.startsWith('<svg') ? (
                    <div className="partner-logo-svg" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }} dangerouslySetInnerHTML={{ __html: partner.logo }} />
                  ) : (
                    <img src={getImageUrl(partner.logo)} alt={partner.name} />
                  )}
                </div>
                <h3>{partner.name}</h3>
                <p>{partner.description}</p>
                {partner.website && (
                  <a href={partner.website} target="_blank" rel="noopener noreferrer" className="partner-link">
                    Visit Website →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR Projects */}
      {projects.map((project, index) => (
        <section key={index} className="project-showcase">
          <div className="container">
            <div className={`project-grid ${index % 2 === 1 ? 'reverse' : ''}`}>
              <div className="project-image">
                <img src={getImageUrl(project.image)} alt={project.title} />
              </div>
              <div className="project-content">
                <span className="partner-tag">CSR Partnership</span>
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <p>{project.details}</p>
                {project.highlights && project.highlights.length > 0 && (
                  <div className="project-highlights">
                    {project.highlights.map((highlight, idx) => (
                      <div key={idx} className="highlight-item">
                        <span className="highlight-number">{highlight.number}</span>
                        <span className="highlight-label">{highlight.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Why Partner With Us */}
      <section className="why-partner-section">
        <div className="container">
          <div className="text-center">
            <span className="section-tag">{sections.collaborations_why_partner_intro.subtitle}</span>
            <h2 className="section-title">{sections.collaborations_why_partner_intro.title}</h2>
            <p className="section-desc">{sections.collaborations_why_partner_intro.description}</p>
          </div>
          <div className="features-grid">
            {whyPartnerFeatures.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="partnership-cta" 
        style={{ 
          backgroundImage: `url('${getBackgroundBanner('collaborations_cta', '/images/cta-bg.jpg')}')` 
        }}
      >
        <div className="container">
          <h2>{sections.collaborations_cta.title}</h2>
          <p>{sections.collaborations_cta.description}</p>
          <div className="cta-buttons">
            <button className="btn btn-primary" onClick={() => { navigate('/'); window.scrollTo(0, 0); }}>Partner With Us →</button>
            <button className="btn btn-outline-light" onClick={() => { navigate('/'); window.scrollTo(0, 0); }}>Get in Touch →</button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Collaborations;
